import React, { useState } from 'react';
import generatePDF, { Resolution, Margin } from 'react-to-pdf';
import type { Options } from 'react-to-pdf';
import { SystemTags } from 'helpers/utils/constantStrings';
import { getModuleBackgroundStyle } from 'helpers/utils/helperFunctions';
import Image from 'frontastic/lib/image';
import defaultLoader from 'frontastic/lib/image/loaders/default';

const options: Options = {
  method: 'save',
  resolution: Resolution.HIGH,
  page: {
    margin: Margin.SMALL,
    format: 'letter',
    orientation: 'portrait',
  },
  canvas: {
    mimeType: 'image/jpeg',
    qualityRatio: 1,
  },
  overrides: {
    pdf: {
      compress: true,
    },
    canvas: {
      useCORS: true,
    },
  },
};

export interface SharePrintProps {
  dataProps?: {
    modulePlaceholder?: {
      internalName?: string;
      moduleName?: string[];
      headline?: string;
      headlineType?: string;
      description?: string;
    };
    moduleNegativeTags?: string[];
  };
  indexing: number;
}

const SharePrintComponent: React.FC<SharePrintProps> = ({ dataProps, indexing }) => {
  const [isShareBoxVisible, setIsShareBoxVisible] = useState(false);
  const currentBackgroundStyle = getModuleBackgroundStyle();

  const getTargetElement = () => document.getElementById('pdf');

  const handlePrint = () => {
    const printContents = document.getElementById('pdf')?.innerHTML || '';
    document.body.innerHTML = printContents;
    window.print();
    window.location.reload();
  };

  const toggleShareBox = () => {
    setIsShareBoxVisible((prev) => !prev);
  };

  const isTagHidden = (tag: string) => dataProps?.moduleNegativeTags?.includes(tag);

  const shareContent = () => {
    const currentURL = window.location.href;
    const title = dataProps?.modulePlaceholder?.headline || '';
    const text = dataProps?.modulePlaceholder?.description || '';

    if (navigator.share) {
      // Use Web Share API for supported devices
      navigator
        .share({
          title: title,
          text: text,
          url: currentURL,
        })
        .then(() => console.log('Content shared successfully!'))
        .catch((error) => console.error('Error sharing content:', error));
    } else {
      // Fallback for unsupported devices
      const encodedURL = encodeURIComponent(currentURL);
      const fallbackURL = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(
        `${text}\n\n${currentURL}`,
      )}`;
      window.open(fallbackURL, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="no-print">
      <div className={`no-print bg-cover px-30 pb-20 pt-50 ${indexing % 2 === 0 ? currentBackgroundStyle : ''}`}>
        {!isTagHidden(SystemTags?.headline) && (
          <div className="w-full text-center text-24 font-bold text-blueTheme-black">
            {dataProps?.modulePlaceholder?.headline}
          </div>
        )}

        <div className="mt-20 flex items-center justify-center gap-60 lg:mt-34 lg:gap-100">
          {/* Share Icon */}
          <div className="relative flex flex-col items-center">
            <Image
              src={'/images/Icon_Teilen_46px_A.png'}
              width={46}
              height={46}
              className="cursor-pointer object-cover"
              alt="Icon_Teilen"
              loader={defaultLoader}
              onClick={toggleShareBox}
            />

            {/* Share Box appears directly below */}
          </div>

          {/* Print Icon */}
          <Image
            src={'/images/Icon_Drucken_46px_A.png'}
            width={46}
            height={46}
            className="cursor-pointer object-cover"
            alt="Icon_Drucken"
            loader={defaultLoader}
            onClick={handlePrint}
          />

          {/* PDF Icon */}
          <Image
            src={'/images/Icon_PDF_46px_A.png'}
            width={46}
            height={46}
            className="cursor-pointer object-cover"
            alt="Icon_PDF"
            loader={defaultLoader}
            onClick={() => generatePDF(getTargetElement, options)}
          />
        </div>
        <div className="mt-10 flex items-center justify-center gap-100">
          <div
            className={`overflow-hidden transition-all duration-500 ease-in-out ${
              isShareBoxVisible ? 'max-h-[60px]' : 'max-h-0'
            }`}
            style={{
              marginTop: isShareBoxVisible ? '10px' : '0',
              height: isShareBoxVisible ? '60px' : '0',
            }} // Adjust height to just above the social media icons (40px icons + 10%)
          >
            {isShareBoxVisible && (
              <div className="w-[320px] rounded-md bg-white p-4 shadow-lg" style={{ minHeight: '60px' }}>
                <div className="flex items-center justify-center gap-30">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={shareContent}
                    className="tooltip icon "
                    data-tooltip="share"
                  >
                    <img src="/images/Share_36px.png" alt="share" width={40} height={40} />
                  </a>
                  <a
                    href={`https://api.whatsapp.com/send?text=${window.location.href}`}
                    data-action="share/whatsapp/share"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tooltip icon"
                    data-tooltip="WhatsApp"
                  >
                    <img src="/images/whatapp.png" alt="WhatsApp" width={40} height={40} />
                  </a>

                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tooltip icon"
                    data-tooltip="Facebook"
                  >
                    <img src="/images/facebook.png" alt="Facebook" width={40} height={40} />
                  </a>
                  <a
                    href={`https://pinterest.com/pin/create/button/?url==${window.location.href}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tooltip icon"
                    data-tooltip="Pinterest"
                  >
                    <img src="/images/pinterest.png" alt="Pinterest" width={40} height={40} />
                  </a>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tooltip icon  "
                    data-tooltip="twitter"
                    href={`https://twitter.com/intent/tweet?text=${window.location.href}`}
                  >
                    <img src="/images/Share_X_36px.png" alt="twitter" width={40} height={40} />
                  </a>
                  <a
                    href={`mailto:?&body=${window.location.href}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tooltip icon"
                    data-tooltip="Mail"
                  >
                    <img src="/images/Share_Mail_36px.png" alt="Mail" width={40} height={40} />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharePrintComponent;
